import { useEffect } from 'react';

import { useUser } from '@clerk/react';

import { useComposeSnapshotIndexStore } from '../store/ComposeSnapshotIndex';
import {
  listDirtyComposeSnapshots,
  pruneComposeSnapshots,
} from '../store/utils/composeSnapshotCache';
import { COMPOSE_SNAPSHOT_REHYDRATE_COALESCE_MS } from '../utils/constants/drafts';
import { VISIBILITY_VISIBLE } from '../utils/constants/uiConstants';

const VISIBILITY_CHANGE_EVENT = 'visibilitychange';
const WINDOW_FOCUS_EVENT = 'focus';

export const useComposeSnapshotIndexHydration = (): void => {
  const { user } = useUser();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) {
      return undefined;
    }
    let isCancelled = false;
    let coalesceTimer: ReturnType<typeof setTimeout> | null = null;
    const hydrate = async () => {
      const snapshots = await listDirtyComposeSnapshots(userId);
      if (!isCancelled) {
        useComposeSnapshotIndexStore.getState().hydrate(userId, snapshots);
      }
    };
    // Returning to the tab fires focus and visibilitychange together: one listing serves both.
    const scheduleHydrate = () => {
      if (coalesceTimer) {
        return;
      }
      coalesceTimer = setTimeout(() => {
        coalesceTimer = null;
        void hydrate();
      }, COMPOSE_SNAPSHOT_REHYDRATE_COALESCE_MS);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === VISIBILITY_VISIBLE) {
        scheduleHydrate();
      }
    };
    const pruneThenHydrate = async () => {
      await pruneComposeSnapshots(
        userId,
        Object.keys(useComposeSnapshotIndexStore.getState().liveSessionIds),
      );
      await hydrate();
    };
    void pruneThenHydrate();
    document.addEventListener(VISIBILITY_CHANGE_EVENT, handleVisibilityChange);
    window.addEventListener(WINDOW_FOCUS_EVENT, scheduleHydrate);
    return () => {
      isCancelled = true;
      if (coalesceTimer) {
        clearTimeout(coalesceTimer);
      }
      document.removeEventListener(
        VISIBILITY_CHANGE_EVENT,
        handleVisibilityChange,
      );
      window.removeEventListener(WINDOW_FOCUS_EVENT, scheduleHydrate);
    };
  }, [userId]);
};
